import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine, Legend,
} from "recharts";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendDown, TrendUp, Calendar, ChartLine, X } from "@phosphor-icons/react";
import NumberInput from "@/components/ui/NumberInput";

const fmtARS = (n) =>
  `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtFechaCorta = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
};

/**
 * Panel de proyección para KPIs decrecientes (Cuentas por Pagar, Pasivos Fin.).
 * Muestra la serie diaria del saldo pendiente en los últimos N días, y proyecta
 * (regresión lineal) cuándo llegaría a cero al ritmo actual.
 */
export default function DashboardProyeccionPanel({ kpi, label, cotizacion = 0 }) {
  const [dias, setDias] = useState(30);
  const [diasInput, setDiasInput] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Punto seleccionado al hacer click en el gráfico (queda fijo abajo).
  const [puntoSel, setPuntoSel] = useState(null);
  // Sólo consolidamos USD→ARS para Pasivos (el que puede tener deuda en USD).
  // Para Cuentas por Pagar el total del KPI es puro ARS, así que no aplica.
  const usarConsolidado = kpi === "pasivos" && Number(cotizacion) > 0;

  const load = async (n) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/serie/${kpi}`, { params: { dias: n } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la serie");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(dias); setPuntoSel(null); /* eslint-disable-next-line */ }, [dias, kpi]);

  // Serializa cada punto convirtiendo USD → ARS con la cotización del dashboard
  // cuando corresponde (así el gráfico refleja el mismo total consolidado que
  // muestra el KPI arriba: "$15.000.000 + USD 200 @1000 = $15.200.000").
  const consolidado = (row) => {
    const ars = Number(row.saldo_ars || 0);
    const usd = Number(row.saldo_usd || 0);
    if (usarConsolidado) return ars + usd * Number(cotizacion || 0);
    return ars;
  };

  const chartData = useMemo(() => {
    if (!data?.serie) return [];
    const puntos = data.serie.map((p) => ({
      fecha: p.fecha,
      fechaCorta: fmtFechaCorta(p.fecha),
      saldo: consolidado(p),
      saldo_ars: Number(p.saldo_ars || 0),
      saldo_usd: Number(p.saldo_usd || 0),
    }));
    // Anexar proyección si existe (siempre en ARS ya que el slope viene en ARS).
    const proy = data.proyeccion;
    const saldoActualConsolidado = puntos.length ? puntos[puntos.length - 1].saldo : 0;
    // Si estamos usando consolidado, extendemos usando el mismo slope diario ARS
    // aplicado al saldo consolidado inicial.
    if (proy?.dias_hasta_cero && proy.dias_hasta_cero > 0 && saldoActualConsolidado > 0) {
      const puntosProy = [];
      // Recalculamos días hasta cero desde el saldo consolidado (no sólo ARS).
      const slopeAbs = Math.abs(proy.slope_diario_ars || 0);
      const diasCeroConsolidado = slopeAbs > 0 ? Math.ceil(saldoActualConsolidado / slopeAbs) : proy.dias_hasta_cero;
      const stepDias = Math.max(1, Math.floor(diasCeroConsolidado / 6));
      let acumDias = 0;
      const lastReal = puntos[puntos.length - 1];
      puntosProy.push({
        fecha: lastReal.fecha,
        fechaCorta: lastReal.fechaCorta,
        proyeccion: lastReal.saldo,
      });
      while (acumDias < diasCeroConsolidado) {
        acumDias = Math.min(acumDias + stepDias, diasCeroConsolidado);
        const dt = new Date(lastReal.fecha);
        dt.setDate(dt.getDate() + acumDias);
        const iso = dt.toISOString().slice(0, 10);
        const restante = Math.max(0, saldoActualConsolidado + acumDias * (proy.slope_diario_ars || 0));
        puntosProy.push({
          fecha: iso,
          fechaCorta: fmtFechaCorta(iso),
          proyeccion: restante,
        });
      }
      return [...puntos, ...puntosProy.slice(1)];
    }
    return puntos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, cotizacion]);

  const proy = data?.proyeccion;
  const saldoActualConsolidado = chartData.length
    ? Number(chartData.find((r) => r.saldo != null && r.fecha === chartData[chartData.length - 1 - (chartData[chartData.length - 1].proyeccion != null ? 6 : 0)]?.fecha)?.saldo ?? chartData[chartData.length - 1]?.saldo ?? 0)
    : 0;
  const saldoActualARS = data?.serie?.length ? Number(data.serie[data.serie.length - 1].saldo_ars || 0) : 0;
  const saldoActualUSD = data?.serie?.length ? Number(data.serie[data.serie.length - 1].saldo_usd || 0) : 0;
  const puedeProyectar = !!proy?.dias_hasta_cero && (usarConsolidado ? saldoActualConsolidado > 0 : saldoActualARS > 0);

  // Detalle del punto seleccionado (busca el registro y su día anterior de tipo Real)
  const detalleSel = useMemo(() => {
    if (!puntoSel || !chartData.length) return null;
    const idx = chartData.findIndex((r) => r.fecha === puntoSel.fecha && (r.saldo != null) === (puntoSel.saldo != null));
    if (idx < 0) return null;
    const p = chartData[idx];
    // Buscar el punto real inmediatamente anterior con dato en la misma serie clickeada
    const key = puntoSel.saldo != null ? "saldo" : "proyeccion";
    let prev = null;
    for (let i = idx - 1; i >= 0; i--) {
      if (chartData[i][key] != null) { prev = chartData[i]; break; }
    }
    const val = Number(p[key] || 0);
    const prevVal = prev ? Number(prev[key] || 0) : null;
    const delta = prevVal == null ? null : val - prevVal;
    return {
      fecha: p.fecha,
      esProyeccion: key === "proyeccion",
      saldo: val,
      saldo_ars: Number(p.saldo_ars || 0),
      saldo_usd: Number(p.saldo_usd || 0),
      delta,
      prevFecha: prev?.fecha || null,
    };
  }, [puntoSel, chartData]);

  const handlePointClick = (payload, serieKey) => {
    if (!payload) return;
    setPuntoSel({
      fecha: payload.fecha,
      saldo: serieKey === "saldo" ? Number(payload.saldo || 0) : null,
      proyeccion: serieKey === "proyeccion" ? Number(payload.proyeccion || 0) : null,
    });
  };

  const isSelected = (row, key) => puntoSel && puntoSel.fecha === row.fecha && (
    (key === "saldo" && puntoSel.saldo != null) || (key === "proyeccion" && puntoSel.proyeccion != null)
  );

  // Custom dot: resalta el punto seleccionado. El click real se maneja vía
  // el onClick de <Line> (Recharts pasa el payload correcto ahí).
  const buildDot = (serieKey, baseColor) => (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || payload?.[serieKey] == null) return null;
    const selected = isSelected(payload, serieKey);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={selected ? 6 : 3.5}
        fill={selected ? "#FFFFFF" : baseColor}
        stroke={baseColor}
        strokeWidth={selected ? 3 : 1}
        style={{ cursor: "pointer", pointerEvents: "all" }}
        onClick={() => handlePointClick(payload, serieKey)}
        data-testid={selected ? `proy-punto-selected-${serieKey}` : undefined}
      />
    );
  };

  // Custom activeDot (el que se muestra al hacer hover): también clickeable
  // para no obligar al usuario a apuntar exactamente al dot pequeño.
  const buildActiveDot = (serieKey, baseColor) => (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill={baseColor}
        stroke="#FFFFFF"
        strokeWidth={2}
        style={{ cursor: "pointer", pointerEvents: "all" }}
        onClick={() => handlePointClick(payload, serieKey)}
      />
    );
  };

  return (
    <div className="border border-[#6B1F24]/20 bg-white rounded-sm p-4 mb-4" data-testid="dashboard-proyeccion-panel">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <ChartLine size={20} weight="duotone" className="text-[#6B1F24]" />
        <h3 className="font-heading font-semibold text-[14px] text-[#6B1F24]">
          Proyección · {label}
        </h3>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11px] uppercase font-mono text-neutral-500">
            Días de historial
          </label>
          <NumberInput
            min={3}
            max={365}
            value={diasInput}
            onChange={(e) => setDiasInput(e.target.value)}
            className="w-20 h-8 text-center font-mono"
            data-testid="proy-dias-input"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={loading}
            onClick={() => {
              const n = parseInt(diasInput, 10);
              if (!n || n < 3 || n > 365) {
                toast.error("Ingresá entre 3 y 365 días");
                return;
              }
              setDias(n);
            }}
            data-testid="proy-recalcular"
          >
            Recalcular
          </Button>
        </div>
      </div>

      {/* Chart */}
      <div style={{ width: "100%", height: 260 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500">
            Cargando serie…
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500">
            Sin datos
          </div>
        ) : (
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#F0EAE0" strokeDasharray="3 3" />
              <XAxis dataKey="fechaCorta" tick={{ fontSize: 11, fontFamily: "monospace" }} />
              <YAxis
                tick={{ fontSize: 11, fontFamily: "monospace" }}
                tickFormatter={(v) => fmtARS(v)}
                width={90}
              />
              <Tooltip
                formatter={(v) => (v == null ? "—" : fmtARS(v))}
                labelFormatter={(l) => l}
                contentStyle={{ fontSize: 12, fontFamily: "monospace" }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#B0B0B0" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="saldo"
                name="Saldo real"
                stroke="#6B1F24"
                strokeWidth={2}
                dot={buildDot("saldo", "#6B1F24")}
                activeDot={buildActiveDot("saldo", "#6B1F24")}
                connectNulls={false}
              />
              {puedeProyectar && (
                <Line
                  type="monotone"
                  dataKey="proyeccion"
                  name="Proyección"
                  stroke="#0EA5A4"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={buildDot("proyeccion", "#0EA5A4")}
                  activeDot={buildActiveDot("proyeccion", "#0EA5A4")}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Detalle del punto seleccionado (click en un dot del gráfico) */}
      {detalleSel && (
        <div
          className={`mt-3 border rounded-sm p-3 ${detalleSel.esProyeccion ? "border-teal-300 bg-teal-50/40" : "border-[#6B1F24]/30 bg-[#FFF8F5]"}`}
          data-testid="proy-detalle-punto"
        >
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar size={16} weight="duotone" className={detalleSel.esProyeccion ? "text-teal-700" : "text-[#6B1F24]"} />
              <div>
                <div className="text-[10px] uppercase font-mono text-neutral-500">
                  {detalleSel.esProyeccion ? "Punto proyectado" : "Saldo al"}
                </div>
                <div className="text-sm font-mono font-bold text-neutral-800" data-testid="proy-detalle-fecha">
                  {(() => {
                    const [y, m, d] = detalleSel.fecha.split("-");
                    return `${d}/${m}/${y}`;
                  })()}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-neutral-500 hover:text-[#6B1F24]"
              onClick={() => setPuntoSel(null)}
              data-testid="proy-detalle-clear"
            >
              <X size={14} className="mr-1" /> Limpiar selección
            </Button>
          </div>

          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border border-neutral-200 rounded p-2 bg-white">
              <div className="text-[10px] uppercase font-mono text-neutral-500">
                {usarConsolidado ? "Saldo consolidado (ARS)" : "Saldo (ARS)"}
              </div>
              <div className={`text-lg font-mono font-bold ${detalleSel.esProyeccion ? "text-teal-700" : "text-[#6B1F24]"}`} data-testid="proy-detalle-saldo">
                {fmtARS(detalleSel.saldo)}
              </div>
              {!detalleSel.esProyeccion && usarConsolidado && (detalleSel.saldo_usd > 0 || detalleSel.saldo_ars > 0) && (
                <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                  ARS {detalleSel.saldo_ars.toLocaleString("es-AR", { maximumFractionDigits: 0 })}
                  {detalleSel.saldo_usd > 0 && (
                    <>
                      {" + USD "}
                      {detalleSel.saldo_usd.toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                      {" @"}
                      {Number(cotizacion || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}
                    </>
                  )}
                </div>
              )}
            </div>

            {!detalleSel.esProyeccion && (
              <div className="border border-neutral-200 rounded p-2 bg-white">
                <div className="text-[10px] uppercase font-mono text-neutral-500">Saldo ARS</div>
                <div className="text-lg font-mono font-bold text-neutral-800">
                  {fmtARS(detalleSel.saldo_ars)}
                </div>
                {detalleSel.saldo_usd > 0 && (
                  <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                    USD {detalleSel.saldo_usd.toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                  </div>
                )}
              </div>
            )}

            <div className="border border-neutral-200 rounded p-2 bg-white">
              <div className="text-[10px] uppercase font-mono text-neutral-500 flex items-center gap-1">
                {detalleSel.delta == null ? null : detalleSel.delta < 0 ? (
                  <TrendDown size={12} className="text-emerald-700" />
                ) : detalleSel.delta > 0 ? (
                  <TrendUp size={12} className="text-rose-700" />
                ) : null}
                Variación vs. día anterior
              </div>
              {detalleSel.delta == null ? (
                <div className="text-sm font-mono text-neutral-500 mt-1">Sin punto previo</div>
              ) : (
                <>
                  <div className={`text-lg font-mono font-bold ${detalleSel.delta < 0 ? "text-emerald-700" : detalleSel.delta > 0 ? "text-rose-700" : "text-neutral-700"}`} data-testid="proy-detalle-delta">
                    {detalleSel.delta > 0 ? "+" : ""}{fmtARS(detalleSel.delta)}
                  </div>
                  {detalleSel.prevFecha && (
                    <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                      vs {(() => { const [y,m,d] = detalleSel.prevFecha.split("-"); return `${d}/${m}/${y}`; })()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {detalleSel.esProyeccion && (
            <div className="mt-2 text-[11px] font-mono text-teal-800 bg-teal-100/60 border border-teal-200 rounded px-2 py-1">
              Valor estimado por regresión lineal al ritmo actual. No es un saldo real registrado.
            </div>
          )}
        </div>
      )}

      {/* Resumen de proyección */}
      {proy && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-neutral-200 rounded p-3 bg-neutral-50">
            <div className="text-[10px] uppercase font-mono text-neutral-500">Saldo actual</div>
            <div className="text-lg font-mono font-bold text-[#6B1F24]">
              {fmtARS(usarConsolidado ? (saldoActualARS + saldoActualUSD * Number(cotizacion || 0)) : saldoActualARS)}
            </div>
            {usarConsolidado && saldoActualUSD > 0 && (
              <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                ARS {saldoActualARS.toLocaleString("es-AR", { maximumFractionDigits: 0 })}
                {" + USD "}
                {saldoActualUSD.toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                {" @"}{Number(cotizacion || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}
              </div>
            )}
            {kpi === "pasivos" && saldoActualUSD > 0 && !usarConsolidado && (
              <div className="text-[10px] font-mono text-rose-700 mt-0.5">
                + USD {saldoActualUSD.toLocaleString("es-AR", { maximumFractionDigits: 2 })} (sin cotización)
              </div>
            )}
          </div>
          <div className="border border-neutral-200 rounded p-3 bg-neutral-50">
            <div className="text-[10px] uppercase font-mono text-neutral-500 flex items-center gap-1">
              {proy.slope_diario_ars < 0 ? (
                <TrendDown size={12} className="text-emerald-700" />
              ) : proy.slope_diario_ars > 0 ? (
                <TrendUp size={12} className="text-rose-700" />
              ) : null}
              Variación diaria promedio
            </div>
            <div className={`text-lg font-mono font-bold ${proy.slope_diario_ars < 0 ? "text-emerald-700" : proy.slope_diario_ars > 0 ? "text-rose-700" : "text-neutral-700"}`}>
              {proy.slope_diario_ars > 0 ? "+" : ""}{fmtARS(proy.slope_diario_ars)}
              <span className="text-[10px] font-normal text-neutral-500 ml-1">/ día</span>
            </div>
          </div>
          <div className="border border-neutral-200 rounded p-3 bg-neutral-50">
            <div className="text-[10px] uppercase font-mono text-neutral-500 flex items-center gap-1">
              <Calendar size={12} /> Estimado saldo cero
            </div>
            {puedeProyectar ? (
              <>
                <div className="text-lg font-mono font-bold text-emerald-700">
                  {(() => {
                    const [y, m, d] = proy.fecha_estimada_cero.split("-");
                    return `${d}/${m}/${y}`;
                  })()}
                </div>
                <div className="text-[11px] font-mono text-neutral-500">
                  en {proy.dias_hasta_cero} día{proy.dias_hasta_cero !== 1 ? "s" : ""}
                </div>
              </>
            ) : proy.pendiente_actual <= 0.01 ? (
              <div className="text-sm font-mono text-emerald-700 mt-1">Ya está en cero ✓</div>
            ) : proy.slope_diario_ars >= 0 ? (
              <div className="text-xs text-rose-700 mt-1 leading-tight">
                La deuda no está bajando. No se puede proyecta un cero al ritmo actual.
              </div>
            ) : (
              <div className="text-xs text-neutral-500 mt-1">Proyección fuera de rango (&gt;5 años)</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
